import React, { useState, useEffect } from 'react'
import MenuComponent from './MenuComponent'
import TextField from 'common/TextField/TextField'
import _ from 'lodash'
import PropTypes from 'prop-types'
import ArrowIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ClearIcon from '@material-ui/icons/Clear'
import DotsLoader from 'common/DotsLoader'

const DropdownComponent = ({
    isLoading,
    descriptionText,
    multiSelect,
    twoLines,
    rightText,
    title,
    subtitle,
    titleVariant = 'default',
    labelInside,
    error,
    disabled,
    label,
    showLabelOptional,
    helperText,
    placeholder,
    readOnly,
    onChange,
    dropdownOptions: rawDropdownOptions = [],
    values = [],
    size,
    name,
    hasDependency = false,
    menuMaxHeight,
    closeOnSelect = true,
    showClearIcon = true,
    isSearchable = true,
    allowUnselectOption = true,
    menuWidth,
    ...restOfProps
}) => {

    const dropdownOptions = Array.isArray(rawDropdownOptions) ? rawDropdownOptions : []

    const [selectedValues, setSelectedValues] = useState(values)
    const [displayValue, setDisplayValue] = useState('')
    const [options, setOptions] = useState(dropdownOptions)
    const [anchorEl, setAnchorEl] = useState(null)
    const textFieldRef = React.useRef(null)

    useEffect(() => {
        setOptions(dropdownOptions)
    }, [dropdownOptions])

    useEffect(() => {
        if (!_.isEqual(values, selectedValues)) {
            setSelectedValues(values)
            if (values[0]?.value === undefined) {
                setDisplayValue('')
            }
        }
        const customDateOption = dropdownOptions.find(option => option.value == 'Fecha personalizada')
        const filterSelectedValue = values.length && values[0]?.value 
        if(name == 'query_date' && filterSelectedValue?.length > 1 && customDateOption){
            customDateOption.from = filterSelectedValue[0]
            customDateOption.to = filterSelectedValue[1]
        }
        else if (name == 'query_date' && customDateOption){
            customDateOption.from = null
            customDateOption.to = null
        }
    }, [values])

    const DotLoaderContainer = () =>{

        return(
            <div>
             <DotsLoader dotClass={'dot-loader-styles'}/>
            </div>
        )
    }

    let iconSecondary

    // Verifica que el dropdown tenga por lo menos un valor seleccionado
    const condition1 = selectedValues.length > 0

    // Verifica que el primer valor de selectedValues NO sea un objeto vacio
    const condition2 = typeof selectedValues[0] === 'object'
        && selectedValues[0] !== null
        && Object.entries(selectedValues[0]).length > 0

    if (isLoading) {
        iconSecondary = DotLoaderContainer
    }
    else if (condition1 && condition2 && showClearIcon) {
        iconSecondary = ClearIcon
    }

    const textFieldProps = {
        'aria-controls': 'simple-menu',
        'aria-haspopup': true,
        labelInside,
        error,
        disabled,
        label,
        showLabelOptional,
        helperText,
        placeholder,
        readOnly,
        size,
        iconSecondary,
        icon: anchorEl ? ExpandLessIcon : ArrowIcon,
        dropdown: true,
        isSearchable,
        tooltip: restOfProps.tooltip,
        actionSecondary: isLoading
            ? ''
            : e => {
                  e.stopPropagation()
                  setSelectedValues([])
              }
    }

    const menuDropdownProps = {
      isLoading,
      descriptionText,
      multiSelect,
      twoLines,
      rightText,
      options,
      title,
      subtitle,
      titleVariant,
      menuMaxHeight,
      closeOnSelect,
      ...restOfProps
    }

    const handleOptionClick = ({ value, label: optionLabel, selectorId }) => {
        // Si la prop multiSelect es true, se van agregando los nuevos objetos al array de valores seleccionados
        // De lo contrario, se establece un array con un solo objeto, que se va reemplazando a medida que se
        // seleccionan diferentes valores.
        if (multiSelect) {
            setSelectedValues(prevState => {
                let newSelectedValues = []
                if(selectorId){
                    newSelectedValues = [...selectedValues.filter(f=>f?.selectorId !== selectorId), { value, label: optionLabel, selectorId }]
                } else {
                    newSelectedValues = [...selectedValues, { value, label: optionLabel }]
                }
                newSelectedValues = _.uniqBy(newSelectedValues, 'value')
                if (_.isEqual(prevState, newSelectedValues)) {
                    newSelectedValues = newSelectedValues.filter(
                        elem => elem.value !== value
                    )
                }
                return newSelectedValues
            })
        } else {
            setSelectedValues(prevState => {
                if (allowUnselectOption && _.isEqual(...prevState, { value, label: optionLabel })) {
                    return []
                }
                return [{ value, label: optionLabel }]
            })
        }
    }

   const showSelectedValue = () => {
        if (multiSelect) {
            if (selectedValues.length === 0) {
                setDisplayValue('')
            } else if (selectedValues.length === 1) {
                setDisplayValue(selectedValues[0]?.label)
            } else {
                setDisplayValue(`${selectedValues.length} Seleccionados`)
            }
        } else {
            if (selectedValues.length === 0) {
                setDisplayValue('')
            } else {
                setDisplayValue(selectedValues[0]?.label)
            }
        }
    }

    useEffect(() => {
        showSelectedValue()
        setOptions(dropdownOptions)
        // se valida que: onChange sea pasado como prop y selectedValues sea distinto a values para que no se ejecute el onChange por defecto.
        // la condición hasDependency (prop) es utilizada para que el onChange se ejecute cuando el dropdown dependa de otro.
         if ((onChange && !_.isEqual(selectedValues, values)) || hasDependency) {
            multiSelect ? onChange(selectedValues) : onChange(selectedValues[0], name)
         }
     }, [selectedValues])


    const handleSearchTextChange = e => {
        if (!isSearchable) return

        let targetValue = e.target.value.toLowerCase()
        setDisplayValue(targetValue)
        setAnchorEl(e.currentTarget)
        const removeAccent = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
        let filteredOptions
        if(targetValue !== '') {
            filteredOptions = dropdownOptions
                .filter(elem => removeAccent(`${elem.label}`).includes(removeAccent(targetValue)))
                .sort((a, b) => a.label.localeCompare(b.label))
        } else {
            filteredOptions = dropdownOptions
            setSelectedValues([])
        }

        setOptions(filteredOptions)
    }

    const handleOpenMenu = e => {
        setAnchorEl(e.currentTarget)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    return (
      <>
        <TextField
          {...textFieldProps}
          onClick={textFieldProps.disabled || handleOpenMenu}
          onChange={handleSearchTextChange}
          placeholder={isLoading ? '' : (textFieldProps?.placeholder || '')}
          ref={textFieldRef}
          autoComplete="off"
          value={displayValue}
          actionSecondary={e => {
            e.preventDefault()
            e.stopPropagation()
            setDisplayValue('')
            setSelectedValues([])
            setOptions(dropdownOptions)
            setAnchorEl(null)
            restOfProps?.handleOptionClick && restOfProps.handleOptionClick([])
          }}
          className={restOfProps.className}
        />
        <MenuComponent
          id="simple-menu"
          selectedValues={selectedValues}
          handleOptionClick={handleOptionClick}
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          handleClose={handleCloseMenu}
          open={Boolean(anchorEl)}
          TransitionProps={{
            onEntered: () => {
              textFieldRef?.current?.focus()
            }
          }}
          {...menuDropdownProps}
          style={{zIndex: '3000'}}
          menuWidth={menuWidth}
        />
      </>
    )
}

DropdownComponent.propTypes = {
    isLoading: PropTypes.bool,
    descriptionText: PropTypes.string,
    multiSelect: PropTypes.bool,
    twoLines:   PropTypes.bool,
    rightText: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    titleVariant: PropTypes.oneOf(['default', 'title2']),
    labelInside: PropTypes.bool,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    showLabelOptional: PropTypes.bool,
    helperText: PropTypes.string,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    onChange: PropTypes.func,
    dropdownOptions: PropTypes.arrayOf(PropTypes.object),
    values: PropTypes.array,
    size: PropTypes.oneOf(['small', 'medium']),
    name: PropTypes.string,
    hasDependency: PropTypes.bool,
    menuMaxHeight: PropTypes.string,
    closeOnSelect: PropTypes.bool,
    buttons: PropTypes.oneOf(['one button', 'two buttons']),
    sizeButtons: PropTypes.oneOf(['small', 'medium']),
    typeSecondaryButton: PropTypes.oneOf(['secondary', 'tertiary']),
    iconSecondary: PropTypes.element,
    icon: PropTypes.element,
    dropdown: PropTypes.bool,
    actionSecondary: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    isSearchable: PropTypes.bool,
}
export default DropdownComponent
